import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import extractVideoId from "@Pages/VideoPage/utils/extractVideoId";
import { addLanguageToPath } from "@Utils/languageCore";
import { getVideoPageSuccess } from "@Pages/VideoPage/data/videoPageReducer";
import { sortVideosWithLiveTourFirst } from "../components/SectionFeatured.tsx/utils";
import gtagEventEmitter from "helpers/gtagEventEmitter";

/**
 * Opens the PDP video modal (rendered by PropertyTopSection) on one room's video:
 * fills `videoPageData`, flips `video_modal`, and pushes `/places/<canonical>/video/<id>`
 * with `window.history` so the PDP stays mounted.
 *
 * GalleryV2, VideoContainer and VirtualTourConnectSectionDesktop still carry their own
 * copy of this; a property-wide playlist can be added here when they migrate.
 */
const useOpenVideoModal = () => {
  const dispatch = useDispatch();
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);

  const { propertyData, propertyReviewData, userLanguage } = useSelector(
    ({ propertyPageData, appData }: AppState) => ({
      propertyData: propertyPageData?.data,
      propertyReviewData: propertyPageData?.reviewData,
      userLanguage: appData.userLanguage || "en",
    }),
    shallowEqual,
  );

  /** `roomTypes` only enriches meta/features; `roomId` scopes the playlist to that room. */
  const openVideoModal = (
    video: any,
    options: { roomTypes?: any[]; eventLabel?: string; roomId: string },
  ) => {
    const { roomTypes = [], eventLabel = "", roomId } = options;
    const videoId = extractVideoId(video?.path);
    if (!videoId || !propertyData?.canonical_name) return false;

    const toRoomVideo = (child: any) => {
      const matchedRoomType = roomTypes.find((rt: any) => rt.id === child.id);
      return (v: any) => ({
        ...v,
        associatedRoomType: {
          name: child.name,
          id: child.id,
          meta: matchedRoomType?.meta || child.meta,
          images: child.images,
          pricing: child.pricing,
          features: matchedRoomType?.features || child.features,
        },
      });
    };

    // Only this room's videos; with none on children_short, just the clicked one.
    const child = propertyData?.children_short?.find((it: any) => it.id === roomId);
    const allVideos: any[] = child?.videos?.length
      ? child.videos.map(toRoomVideo(child))
      : [child ? toRoomVideo(child)(video) : { ...video, associatedRoomType: null }];

    const reviews = [
      ...(propertyReviewData?.result || []),
      ...(propertyReviewData?.resultWithoutMedia || []),
    ];

    dispatch(
      getVideoPageSuccess({
        data: {
          id: propertyData?.id,
          canonical_name: propertyData?.canonical_name,
          name: propertyData?.name,
          reviews_rating: propertyData?.reviews_rating,
          reviews_count: propertyData?.reviews_count,
          images: propertyData?.images?.slice(0, 1) || [],
        },
        reviewData: reviews,
        videos: sortVideosWithLiveTourFirst(allVideos),
        selectedVideoId: videoId,
        dataSource: "gallery",
      }),
    );

    propertyPageDispatcher.showVideoModal({ show: true, videoId });

    if (__CLIENT__) {
      window.history.pushState(
        { videoModal: true },
        "",
        addLanguageToPath(`/places/${propertyData?.canonical_name}/video/${videoId}`, userLanguage),
      );
    }

    gtagEventEmitter({
      action: "videos_clicked",
      category: "inventory_page",
      label: `videos, ${eventLabel || propertyData?.name}`,
    });

    return true;
  };

  return openVideoModal;
};

export default useOpenVideoModal;
